import { __ } from '@wordpress/i18n';
import { useState, useEffect } from '@wordpress/element';
import { Button, Spinner, Notice, TextControl } from '@wordpress/components';

const StockImagesTab = ({ refreshStats }) => {
    const [query, setQuery] = useState('');
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(null);
    const [notice, setNotice] = useState(null);
    const [page, setPage] = useState(1);
    const [totalResults, setTotalResults] = useState(0);

    useEffect(() => {
        fetchImages(1);
    }, []);

    const fetchImages = async (pageNum = 1, searchQuery = query) => {
        setLoading(true);
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_stock_images',
                    nonce: mtrimAdminData.nonce,
                    query: searchQuery,
                    page: pageNum,
                }),
            });
            const data = await response.json();
            if (data.success) {
                if (pageNum === 1) {
                    setImages(data.data.photos || []);
                } else {
                    setImages([...images, ...(data.data.photos || [])]);
                }
                setTotalResults(data.data.total_results || 0);
                setPage(pageNum);
            } else {
                setNotice({ type: 'error', message: data.data?.message || __('Failed to fetch images', 'media-trim') });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Error fetching images', 'media-trim') });
        }
        setLoading(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        fetchImages(1);
    };

    const importImage = async (image) => {
        setImporting(image.id);
        try {
            const response = await fetch(mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_import_stock_image',
                    nonce: mtrimAdminData.nonce,
                    image_url: image.src.large2x || image.src.original,
                    title: `Pexels - ${image.alt || image.id}`,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: __('Image added to Media Library!', 'media-trim') });
                refreshStats();
            } else {
                setNotice({ type: 'error', message: data.data?.message || __('Failed to import image', 'media-trim') });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Error importing image', 'media-trim') });
        }
        setImporting(null);
    };

    const loadMore = () => {
        fetchImages(page + 1);
    };

    return (
        <div className="mtrim-stock-images-tab">
            <div className="mtrim-section-header">
                <h2>{__('Stock Images', 'media-trim')}</h2>
                <p>{__('Search and import free high-quality photos from Pexels directly to your Media Library.', 'media-trim')}</p>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    onRemove={() => setNotice(null)}
                    style={{ marginBottom: '20px' }}
                >
                    {notice.message}
                </Notice>
            )}

            <form className="mtrim-search-form" onSubmit={handleSearch} style={{ marginBottom: '30px', display: 'flex', gap: '10px' }}>
                <div style={{ flexGrow: 1 }}>
                    <TextControl
                        value={query}
                        onChange={setQuery}
                        placeholder={__('Search for photos (e.g. nature, office, technology)...', 'media-trim')}
                    />
                </div>
                <Button variant="primary" type="submit" disabled={loading}>
                    {loading ? <Spinner /> : __('Search', 'media-trim')}
                </Button>
            </form>

            <div className="mtrim-image-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', 
                gap: '20px' 
            }}>
                {images.map((image) => (
                    <div key={image.id} className="mtrim-image-card" style={{
                        background: '#fff',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div className="mtrim-image-preview" style={{ position: 'relative', paddingTop: '66.66%' }}>
                            <img 
                                src={image.src.medium} 
                                alt={image.alt} 
                                style={{ 
                                    position: 'absolute', 
                                    top: 0, 
                                    left: 0, 
                                    width: '100%', 
                                    height: '100%', 
                                    objectFit: 'cover' 
                                }} 
                            />
                        </div>
                        <div className="mtrim-image-info" style={{ padding: '15px' }}>
                            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: '#666', height: '40px', overflow: 'hidden' }}>
                                {image.alt || __('Untitled Image', 'media-trim')}
                            </p>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '11px', color: '#999' }}>
                                    by <a href={image.photographer_url} target="_blank" rel="nofollow">{image.photographer}</a>
                                </span>
                                <Button 
                                    variant="secondary" 
                                    size="small"
                                    onClick={() => importImage(image)}
                                    disabled={importing === image.id}
                                >
                                    {importing === image.id ? <Spinner /> : __('Import', 'media-trim')}
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {loading && images.length > 0 && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spinner />
                </div>
            )}

            {!loading && images.length > 0 && images.length < totalResults && (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Button variant="secondary" onClick={loadMore}>
                        {__('Load More', 'media-trim')}
                    </Button>
                </div>
            )}

            {!loading && images.length === 0 && (
                <div className="mtrim-no-results" style={{ textAlign: 'center', padding: '60px', background: '#f9f9f9', borderRadius: '8px' }}>
                    <h3>{__('No images found', 'media-trim')}</h3>
                    <p>{__('Try a different search term.', 'media-trim')}</p>
                </div>
            )}
        </div>
    );
};

export default StockImagesTab;
